// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Escape backslashes and every control character to a visible form.
 *
 * Written as a charCode loop rather than a regex character class on purpose: a control-character
 * range is easy to get subtly wrong, and a source file containing literal control bytes is a
 * nightmare to review. This is not a hot path — it runs once per emitted CSV field.
 */
function escapeControls(v: string): string {
  let out = '';
  for (let i = 0; i < v.length; i++) {
    const c = v.charCodeAt(i);
    if (c === 92) out += '\\\\';
    else if (c === 13) out += '\\r';
    else if (c === 10) out += '\\n';
    else if (c === 9) out += '\\t';
    else if (c < 32 || c === 127) out += '\\x' + c.toString(16).padStart(2, '0');
    else out += v[i];
  }
  return out;
}

/**
 * RFC 4180 plus three pieces of hardening that matter for a file whose entire job is to be
 * grepped, and then opened in Excel by a security lead.
 *
 *  - Control characters become VISIBLE escapes, so one finding is always exactly one physical
 *    line. This is a deliberate deviation from RFC 4180's quoted-newline rule: a findings.csv
 *    whose row count differs from its line count cannot be `wc -l`'d or `grep`'d, and being
 *    grepped is the point of the file.
 *  - `;` triggers quoting, because German, French and Spanish Excel locales split on it.
 *  - A leading `=`, `+`, `-` or `@` gets a `'` prefix. CSV formula injection is a real path from
 *    "here is a findings report" to code execution on the reviewer's machine.
 */
export function csvField(v: string): string {
  let s = escapeControls(v);

  if (/^[=+\-@]/.test(s)) s = "'" + s;

  if (/[",;]|^\s|\s$/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

export function csvRow(fields: readonly string[]): string {
  return fields.map(csvField).join(',') + '\r\n';
}

export const FINDINGS_HEADER = [
  'record',
  'line',
  'byte_offset',
  'json_pointer',
  'in_key',
  'rule',
  'severity',
  'tier',
  'match_len',
  'validator',
  'action',
  'replacement',
  'suppressed_by',
  'context',
] as const;

export type FindingsColumn = (typeof FINDINGS_HEADER)[number];

export interface FindingRow {
  readonly record: number;
  readonly line: number;
  readonly byteOffset: number;
  readonly pointer: string;
  readonly inKey: boolean;
  readonly rule: string;
  readonly severity: string;
  readonly tier: number;
  readonly matchLen: number;
  readonly validator: string;
  readonly action: string;
  readonly replacement: string;
  readonly suppressedBy: string;
  readonly context: string;
}

export interface CsvOptions {
  readonly includeContext: boolean;
  readonly includeMatchLen: boolean;
}

/** UTF-8 BOM, or Excel on Windows mojibakes every non-ASCII context window. */
export const CSV_BOM = '﻿';

export function findingsColumns(opts: CsvOptions): FindingsColumn[] {
  return FINDINGS_HEADER.filter(
    (h) => (h !== 'context' || opts.includeContext) && (h !== 'match_len' || opts.includeMatchLen),
  );
}

export function* findingsCsv(
  rows: Iterable<FindingRow>,
  opts: CsvOptions,
): Generator<string, void, void> {
  const header = findingsColumns(opts);
  yield CSV_BOM + csvRow(header);
  for (const r of rows) {
    const all: Record<FindingsColumn, string> = {
      record: String(r.record),
      line: String(r.line),
      byte_offset: String(r.byteOffset),
      json_pointer: r.pointer,
      in_key: r.inKey ? 'yes' : 'no',
      rule: r.rule,
      severity: r.severity,
      tier: String(r.tier),
      match_len: String(r.matchLen),
      validator: r.validator,
      action: r.action,
      replacement: r.replacement,
      suppressed_by: r.suppressedBy,
      context: r.context,
    };
    yield csvRow(header.map((h) => all[h]));
  }
}
